import * as React from 'react';

import {getCommaSeparatedList} from 'nutshell-core/get-comma-separated-list';

import {colors} from 'shells/colors/colors';
import {SelectionTable} from 'shells/table/selection-table';
import {shouldShowDelimiter} from 'shells/utils/should-show-delimiter';

import type {MeetingForLandingPage as Meeting} from 'nutshell-graphql-types';

import {getStatusBadge} from '../../../../scheduler/helpers';

import {useManageSpecialState} from '../consts';
import type {SelectedItem, SpecialState} from '../custom-add-on-select-modal';
import {useGetMeetingsForLandingPages} from '../graphql/hooks/use-get-meetings-for-landing-pages';

type Props = {
    setSelectedItem: (item: SelectedItem) => void;
    selectedItem: SelectedItem | null | undefined;
    setSpecialState: (specialState?: SpecialState | null | undefined) => void;
};

export function MeetingSelectionTable(props: Props) {
    const {setSelectedItem, selectedItem, setSpecialState} = props;

    const {meetings, isLoading, isFirstLoad, error} = useGetMeetingsForLandingPages();

    const isEmptyState = !isLoading && !error && !meetings.length;

    useManageSpecialState(isLoading, isFirstLoad, isEmptyState, setSpecialState);

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (meeting: Meeting) => meeting.name,
                dataType: 'string',
                shouldTruncate: true,
                width: 140,
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (meeting: Meeting) => getStatusBadge(meeting),
                dataType: 'statusBadge',
            },
            {
                id: 'creator',
                Header: 'Host(s)',
                accessor: (meeting: Meeting) => meeting.creator,
                dataType: 'component',
                width: 150,
                // @ts-expect-error
                sortType: (rowA, rowB) => {
                    // TODO UPDATE
                    return rowA.original.creator.name >= rowB.original.creator.name ? 1 : -1;
                },
                Cell: ({cell}: any) => {
                    if (cell.row.original.meetingType === 'ROUND_ROBIN' && cell.row.original.team) {
                        return (
                            <div style={{color: colors.textUsers}}>
                                {cell.row.original.team.name}
                            </div>
                        );
                    }

                    const allHosts = [cell.value, ...cell.row.original.cohosts];

                    return (
                        <div
                            className='truncate'
                            title={getCommaSeparatedList(allHosts.map((cohost) => cohost.name))}
                        >
                            {allHosts.map((cohost, index) => (
                                <span key={cohost.id}>
                                    <span style={{color: colors.textUsers}}>{cohost.name}</span>
                                    {shouldShowDelimiter(index, allHosts.length) ? (
                                        <>,&nbsp;</>
                                    ) : undefined}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                id: 'activityType',
                Header: 'Activity type',
                accessor: (meeting: Meeting) => meeting.activityType.name,
                dataType: 'string',
                width: 110,
            },
            {
                id: 'durationMinutes',
                Header: 'Duration',
                accessor: (meeting: Meeting) => meeting.durationMinutes,
                dataType: 'duration',
            },
            {
                id: 'bookings',
                Header: '# Bookings',
                accessor: (meeting: Meeting) => meeting.bookings.length,
                dataType: 'integer',
                width: 90,
            },
        ],
        []
    );

    return !isFirstLoad && !isEmptyState ? (
        <SelectionTable
            hasHeaderLeftBorder={false}
            hasHeaderRightBorder={false}
            hasHeaderTopBorder={false}
            // @ts-expect-error
            columns={columns}
            data={meetings}
            isLoading={isLoading}
            error={error}
            fullWidth={true}
            onSelectRow={(row) => {
                setSelectedItem({
                    value: row.original.id,
                    name: row.original.name,
                });
            }}
            selectedRowId={selectedItem ? selectedItem.value : null}
        />
    ) : null;
}
